import { Switch, Route, Router, useLocation } from "wouter";
import { useHashLocation } from "wouter/use-hash-location";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import WhatsNewDrawer from "@/components/WhatsNewDrawer";
import AccountList from "@/pages/AccountList";
import AccountScorecard from "@/pages/AccountScorecard";
import DimensionFlow from "@/pages/DimensionFlow";
import Manager from "@/pages/Manager";
import Executive from "@/pages/Executive";
import Pipeline from "@/pages/Pipeline";
import Daily from "@/pages/Daily";
import Coaching from "@/pages/Coaching";
import CompellingEvents from "@/pages/CompellingEvents";
import PlanPresentation from "@/pages/PlanPresentation";
import NewAccount from "@/pages/NewAccount";
import PlanReport from "@/pages/PlanReport";
import QuestionTree from "@/pages/QuestionTree";
// W66.D: DecisionTree import removed — route deleted (/decision-tree → 404).
import QuestionBank from "@/pages/QuestionBank";
import SalesPath from "@/pages/SalesPath";
// W66.D: BdrSalesPath import removed — route deleted (/bdr-sales-path → 404).
import Leaderboard from "@/pages/Leaderboard";
import PlanVsActual from "@/pages/PlanVsActual";
import SalesPlanWorkbook from "@/pages/SalesPlanWorkbook";
import SalesPlanTopTen from "@/pages/SalesPlanTopTen"; // W66.W6
import QuestionReview from "@/pages/QuestionReview";
import AuditReview from "@/pages/AuditReview";
import AuditReviewV2 from "@/pages/AuditReviewV2";
import DashboardPreview from "@/pages/DashboardPreview";
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import PilotLogin from "@/pages/PilotLogin";
import SettingsHub from "@/pages/settings/SettingsHub";
import LinkExpired from "@/pages/link-expired";
import { useMagicLinkError } from "@/hooks/use-magic-link-error";
import { usePasswordRecoveryRoute } from "@/hooks/use-password-recovery-route";
import Dashboards from "@/pages/Dashboards";
import BdrView from "@/pages/dashboards/BdrView";
import AeView from "@/pages/dashboards/AeView";
import ManagerView from "@/pages/dashboards/ManagerView";
import ExecutiveView from "@/pages/dashboards/ExecutiveView";
import MarketingView from "@/pages/dashboards/MarketingView";
import MarketingDashboardShell from "@/pages/dashboards/MarketingDashboardShell"; // W48
import RapidIntake from "@/pages/RapidIntake";
import BdrActivityLog from "@/pages/log/BdrActivityLog";
import CadenceQueue from "@/pages/cadence/CadenceQueue";
import CadencePage from "@/pages/Cadence"; // W37-A: BDR Cadence Queue — /cadence direct route
import CadenceBuilder from "@/pages/cadence/CadenceBuilder";
import CadenceReporting from "@/pages/cadence/CadenceReporting";
import EmailSettings from "@/pages/settings/EmailSettings";
import OverridePolicy from "@/pages/settings/OverridePolicy";
import Integrations from "@/pages/settings/Integrations";
import AccountSettings from "@/pages/settings/AccountSettings";
import TodaysBrief from "@/pages/TodaysBrief";
import TodayBriefPage from "@/pages/TodayBriefPage";
import AeDaily from "@/pages/daily/AeDaily";
import BdrDaily from "@/pages/daily/BdrDaily";
import AeTodayCommandCenter from "@/pages/AeTodayCommandCenter";
import DNAPage from "@/pages/DNAPage";
import AeActivityLog from "@/pages/log/AeActivityLog";
import NotFound from "@/pages/not-found";
import ComingSoon from "@/pages/ComingSoon";
import Methodology from "@/pages/Methodology";
import Flowcharts from "@/pages/Flowcharts";
import AddLead from "@/pages/leads/AddLead";
import UploadContacts from "@/pages/contacts/UploadContacts";
import HoldingQueue from "@/pages/contacts/HoldingQueue";
import PlansHome from "@/pages/plans/PlansHome";
import ForecastView from "@/pages/forecast/ForecastView";
import ReportsHome from "@/pages/reports/ReportsHome";
import DashboardsIndex from "@/pages/dashboards/index"; // W46 — chart-first dashboards
import AdminHome from "@/pages/admin/AdminHome";
import AuditLogView from "@/pages/admin/AuditLogView";
import DisciplineAdminPage from "@/pages/admin/DisciplineAdminPage";
import ActivityAnomalies from "@/pages/admin/ActivityAnomalies";
import ActivityCapturePolicy from "@/pages/admin/ActivityCapturePolicy";
import CompanySettings from "@/pages/settings/CompanySettings";
import TermsOfService from "@/pages/legal/TermsOfService";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import DocumentTitle from "@/components/DocumentTitle";
import CalendarView from "@/pages/calendar/CalendarView";
// W66.D: HooksLibrary import removed — route deleted (/hooks → 404; use /marketing/hooks).
import ActivityLogPage from "@/pages/activity/ActivityLogPage";
import ActivityAuditPage from "@/pages/activity/ActivityAuditPage";
import ActivityAuditDrilldown from "@/pages/activity/ActivityAuditDrilldown";
import LeadQualify from "@/pages/leads/LeadQualify";
import LeadAssign from "@/pages/leads/LeadAssign";
import LeadSuccess from "@/pages/leads/LeadSuccess";
import RouteBdr from "@/pages/leads/RouteBdr"; // P0-5 — BDR routing step
import AnswerLane from "@/pages/accounts/AnswerLane"; // W21: Primary Answer Surface
import RapidAnswer from "@/pages/RapidAnswer"; // W54: Rapid Answer Mode
// W66.D: GalaxyPlaceholder import removed — /wall-poster route deleted (/galaxy is canonical).
import Galaxy from "@/pages/Galaxy"; // W4-Galaxy: live 3D galaxy + account path replay
// W66.B — Operator Workspace
import WorkspacePage from "@/pages/workspace";
// W66.F — Onboarding flow (first-login)
import OnboardingPage from "@/pages/onboarding";
// onboarding/pilot-v1 — Invitation acceptance
import AcceptInvite from "@/pages/AcceptInvite";
// W66.H — Real Funnel
import FunnelPage from "@/pages/funnel";
// W65 — Opportunity pages
import OpportunitiesIndex from "@/pages/opportunities/index";
import OpportunityDetail from "@/pages/opportunities/detail";
import NewOpportunity from "@/pages/opportunities/new";
// W66.G — Organizational Mapping
import AccountOrgMap from "@/pages/account-org-map";
// W66.J — Jumpstart 30/60/90
import JumpstartPage from "@/pages/jumpstart";
import Tour from "@/pages/Tour"; // W66.X16 — in-platform guided tour

// ─── Reporting deep-views (management dashboards) ──────────────────────────────
import ReportingHub from "@/pages/reporting/ReportingHub";
import RepSelector from "@/pages/reporting/RepSelector"; // W11: bare /reporting/rep route
import TeamScoreboard from "@/pages/reporting/TeamScoreboard";
import RepDetail from "@/pages/reporting/RepDetail";
import AccountsInFlight from "@/pages/reporting/AccountsInFlight";
import SourceFunnel from "@/pages/reporting/SourceFunnel";
import DataHygiene from "@/pages/reporting/DataHygiene";
import AccountDetail from "@/pages/reporting/AccountDetail";
import HelixScoreDemo from "@/pages/helix-demo/HelixScoreDemo";
// ─── Reports (executive-tier deep-dives) ────────────────────────────────────
import SourceAttribution from "@/pages/reports/SourceAttribution";
import RepQuota from "@/pages/reports/RepQuota";
import CompositeScoringReport from "@/pages/reports/CompositeScoringReport";
import ForecastRollupReport from "@/pages/reports/ForecastRollupReport";
import EscalationQueueReport from "@/pages/reports/EscalationQueueReport";
import PushAdequacyReport from "@/pages/reports/PushAdequacyReport";
import PushReentryReport from "@/pages/reports/PushReentryReport";
import ExternalConsultantsReport from "@/pages/reports/ExternalConsultantsReport";
import SoftStopAuditReport from "@/pages/reports/SoftStopAuditReport";
import SoftStopQueue from "@/pages/SoftStopQueue";
// ──────────────────────────────────────────────────────────────────────────────
import MarketingPlan from "@/pages/marketing/MarketingPlan";
import MarketingCalendar from "@/pages/marketing/MarketingCalendar";
import MarketingHooks from "@/pages/marketing/MarketingHooks";
import MarketingHookDetail from "@/pages/marketing/MarketingHookDetail";
import MarketingEventLog from "@/pages/marketing/MarketingEventLog";
import MarketingActivityLog from "@/pages/log/MarketingActivityLog";
// W66.D: DocumentLibrary import removed — /library route deleted (orphaned, no active use).
import TemplateLibrary from "@/pages/TemplateLibrary";
import ManualsHome from "@/pages/manuals/ManualsHome";
import MarketingManual from "@/pages/manuals/MarketingManual";
import BdrManual from "@/pages/manuals/BdrManual";
import AeManual from "@/pages/manuals/AeManual";
import ManagerManual from "@/pages/manuals/ManagerManual";
import ExecutiveManual from "@/pages/manuals/ExecutiveManual";
import MyPlanPage from "@/pages/MyPlanPage";
import TrendPage from "@/pages/TrendPage";
import MarketingAssistPage from "@/pages/MarketingAssistPage";
import MarketingHome from "@/pages/MarketingHome"; // W55
import PipelineDNA from "@/pages/PipelineDNA";
import TeamDNA from "@/pages/TeamDNA";
import TimingGateSmokeDemo from "@/pages/dev/TimingGateSmokeDemo"; // S-CP10-4 dev smoke route
// W66.X17: Reference Library — doctrine, manifestos, CP models
import ReferenceLibrary from "@/pages/library/ReferenceLibrary";
import ReferenceArticle from "@/pages/library/ReferenceArticle";
// 2026-06-11: LinkedIn articles by Bryan K. Short inside Reference Library
import LinkedInArticle from "@/pages/library/LinkedInArticle";
import ManagerHub from "@/pages/dashboards/ManagerHub";
import ManagerLegacyRetired from "@/pages/ManagerLegacyRetired"; // W11: replaced hanging Manager.tsx
import HelpButton from "@/components/HelpButton";
import WebsitePortalButton from "@/components/WebsitePortalButton";
// D-079 (2026-05-22): QuickScoreFab removed globally. Quick Score belongs in
// exactly two places per canon: (1) BDR Suspect->Prospect promotion gate and
// (2) the public website 7-card lead capture flow. The floating button
// surfaced it on every account scorecard, violating that boundary.
// import QuickScoreFab from "@/components/calculator/QuickScoreFab";
import OpportunityCalculatorPage from "@/pages/OpportunityCalculatorPage";
import OpportunityCalculatorPageV3 from "@/pages/OpportunityCalculatorPageV3";
import Tower from "@/pages/Tower";
import PipelineTower from "@/pages/PipelineTower";
import CommandViewPage, { AccountCommandViewPage } from "@/pages/command-view"; // W45.1 / W45.2
import InstallPrompt from "@/components/InstallPrompt";
import SupportQueue from "@/pages/admin/SupportQueue";
import { TourProvider } from "@/lib/tour-store";
import { SprintViewProvider } from "@/lib/sprint-context";

// Wave I (B-BDR6): Singleton QueryClient. Exported so non-hook impersonation
// handlers can call queryClient.clear() before window.location.reload().
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});
import { TourOverlay } from "@/components/tour/TourOverlay";
import { useEffect, useState } from "react";
import { useLocation as useWouterLocation } from "wouter";
import { getSession, restoreSession, supabase, logout } from "@/lib/session";
import { getImpersonationTarget, subscribe as subscribeImpersonation } from "@/lib/impersonation";
import { runSilentRecovery } from "@/lib/recovery";

/**
 * Instant hash-router redirect. Navigates on mount, renders nothing.
 */
function Redirect({ to }: { to: string }) {
  const [, setLoc] = useWouterLocation();
  useEffect(() => { setLoc(to); }, [setLoc, to]);
  return null;
}

/**
 * LogoutRoute — /#/logout direct route.
 * Performs the full logout flow then redirects to /#/login.
 * Useful for emergency logout via URL in PWA standalone mode.
 */
function LogoutRoute() {
  const [, setLoc] = useWouterLocation();
  useEffect(() => {
    (async () => {
      try {
        await logout();
      } catch {
        // Even on error, redirect to login.
      }
      setLoc("/login");
    })();
  }, [setLoc]);
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: "#FAFAF7" }}
      aria-live="polite"
    >
      <span style={{ color: "#6B6B70", fontSize: "14px" }}>Signing out...</span>
    </div>
  );
}

/**
 * Root redirect: restores Supabase session on load then routes by role.
 */
function RootRoute() {
  const [, setLoc] = useWouterLocation();
  const [checking, setChecking] = useState(true);
  // Day 0: unauthenticated visitors see the Attilius standalone hero at "/"
  // instead of being bounced straight to the sign-in card.
  const [showLanding, setShowLanding] = useState(false);

  useEffect(() => {
    (async () => {
      // Check for existing in-memory session first.
      let s = getSession();
      if (!s) {
        // Attempt to restore from Supabase's persisted session (localStorage).
        s = await restoreSession();
      }
      if (s) {
        // W66.F: first-login onboarding check — if onboarding_completed_at is null, send to /onboarding.
        if (!s.onboardingCompletedAt) {
          setLoc("/onboarding");
          setChecking(false);
          return;
        }
        // Route by role — planning-trend-marketing 2026-05-08:
        // Marketing -> /marketing, AE/BDR -> /my-plan, Manager -> /manager (Trend default),
        // Executive -> /executive (roll-up default).
        if (s.role === "executive") setLoc("/executive");
        else if (s.role === "marketing") setLoc("/marketing");
        else if (s.role === "se") setLoc("/dashboards/se");
        else if (s.role === "manager" && s.isDualRole) setLoc("/executive");
        else if (s.role === "manager") setLoc("/manager");
        else if (s.role === "ae") setLoc("/my-plan");
        else if (s.role === "bdr") setLoc("/my-plan");
        else setLoc("/dashboards");
      } else {
        // Day 0: no session — render the public Attilius hero at "/".
        setShowLanding(true);
      }
      setChecking(false);
    })();
  }, [setLoc]);

  if (checking) {
    return <div className="min-h-screen bg-background" aria-hidden="true" />;
  }
  return showLanding ? <Landing /> : null;
}

/**
 * Guard: redirects unauthenticated users to /pilot/login. When `requireRole`
 * is supplied, signed-in users whose role isn't in the list are shown a
 * fail-soft panel with a link back to their home dashboard — no hard 403.
 * Server endpoints stay role-gated independently — this guard is a
 * client-visibility layer.
 *
 * W9: replaced redirect-with-toast with fail-soft inline panel.
 */
type GuardRole = "manager" | "executive" | "bdr" | "ae" | "marketing" | "se";

/** Map a role to its home dashboard path. */
function roleHomePath(role: GuardRole): string {
  if (role === "executive") return "/executive";
  if (role === "manager") return "/manager";
  if (role === "marketing") return "/marketing";
  if (role === "bdr" || role === "ae") return "/my-plan";
  return "/dashboards";
}

/** Human-readable label for a role. */
function roleLabel(role: GuardRole): string {
  if (role === "bdr") return "BDR";
  if (role === "ae") return "AE";
  if (role === "manager") return "Manager";
  if (role === "executive") return "Executive";
  if (role === "marketing") return "Marketing";
  if (role === "se") return "SE";
  return role;
}

/** Human-readable label for the required roles. */
function requiredRoleLabel(requireRole: GuardRole[]): string {
  if (requireRole.includes("executive") && requireRole.includes("manager")) return "Manager or Executive";
  if (requireRole.length === 1) return roleLabel(requireRole[0]);
  return requireRole.map(roleLabel).join(" or ");
}

/**
 * W9: Fail-soft panel rendered when role check fails.
 * No redirect, no 403 — polite inline message with a link home.
 */
function RoleMismatchPanel({ role, requireRole }: { role: GuardRole; requireRole: GuardRole[] }) {
  const homePath = roleHomePath(role);
  const homeLabel = role === "bdr" ? "BDR dashboard" : role === "ae" ? "AE dashboard" : "your dashboard";
  const requiredLabel = requiredRoleLabel(requireRole);
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FAFAF7",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Inter, system-ui, sans-serif",
        padding: "24px",
      }}
    >
      <div
        style={{
          maxWidth: 420,
          background: "#FFFFFF",
          border: "1px solid #E5E5E7",
          borderRadius: 12,
          padding: "32px 28px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "#F4F4F5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="10" cy="10" r="9" stroke="#6B6B70" strokeWidth="1.5" />
            <path d="M10 6v5" stroke="#6B6B70" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="10" cy="14" r="0.75" fill="#6B6B70" />
          </svg>
        </div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#8E8E94",
            marginBottom: 8,
          }}
        >
          Access restricted
        </div>
        <p style={{ fontSize: 15, fontWeight: 600, color: "#0B0B0F", margin: "0 0 8px" }}>
          This view is for the {requiredLabel} role.
        </p>
        <p style={{ fontSize: 13, color: "#6B6B70", margin: "0 0 24px", lineHeight: 1.5 }}>
          You are signed in as {roleLabel(role)}. This section is not available for your role.
        </p>
        <a
          href={`#${homePath}`}
          style={{
            display: "inline-block",
            padding: "10px 20px",
            background: "#0B0B0F",
            color: "#FFFFFF",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          Return to {homeLabel}
        </a>
      </div>
    </div>
  );
}

function AuthGuard({
  children,
  requireRole,
}: {
  children: React.ReactNode;
  requireRole?: GuardRole[];
}) {
  const [loc, setLoc] = useWouterLocation();
  const [ready, setReady] = useState(false);
  const [role, setRole] = useState<GuardRole | null>(null);

  useEffect(() => {
    (async () => {
      let s = getSession();
      if (!s) {
        s = await restoreSession();
      }
      if (!s) {
        try {
          if (loc && loc !== "/login" && loc !== "/pilot/login") {
            sessionStorage.setItem("aip:postLoginRedirect", loc);
          }
        } catch {}
        // D-052: canonical login URL is /#/login (/#/pilot/login remains a valid alias)
        setLoc("/login");
      } else {
        setRole(s.role as GuardRole);
        setReady(true);
      }
    })();
  }, [setLoc, loc]);

  if (!ready) return <div className="min-h-screen bg-background" aria-hidden="true" />;

  // W9: fail-soft role mismatch — render polite panel instead of hard redirect.
  if (requireRole && role && !requireRole.includes(role)) {
    return <RoleMismatchPanel role={role} requireRole={requireRole} />;
  }

  return <>{children}<HelpButton /><WebsitePortalButton /></>;
}

/**
 * DailyRouter — role-gates the /#/daily route.
 * AE → AeDaily (personal queue), BDR → BdrDaily (personal queue),
 * manager/executive → Daily (team rollup). Other roles are blocked.
 */
function DailyRouter() {
  const session = getSession();
  if (session?.role === "ae") {
    return <AuthGuard><AeDaily /></AuthGuard>;
  }
  if (session?.role === "bdr") {
    return <AuthGuard><BdrDaily /></AuthGuard>;
  }
  // manager / executive → team rollup; gate with requireRole so
  // unauthenticated users hit login and other roles see the polite panel.
  return <AuthGuard requireRole={["manager", "executive"]}><Daily /></AuthGuard>;
}

/**
 * TodayRouter — W40: AE Today 6-Section Command Center for all roles.
 *
 * All authenticated users land on AeTodayCommandCenter (W40 spec).
 * The component uses OperatorShell so brand chrome and nav are always present.
 *
 * Impersonation: when a Manager impersonates an AE or BDR, the same command
 * center renders — the server scopes data by the session user's effective role.
 *
 * Legacy AeDaily (role-scoped AE-only view) is preserved at /#/daily for AEs.
 * BdrDaily is preserved at /#/daily for BDRs via DailyRouter.
 */
/**
 * W44.1: GalaxyPathRecorder — tracks the last non-galaxy hash path so that
 * the "Exit Galaxy" pill in Galaxy.tsx can navigate back to the prior route.
 * Rendered inside the Router so useLocation works correctly.
 */
function GalaxyPathRecorder() {
  const [loc] = useLocation();
  useEffect(() => {
    if (!loc.startsWith("/galaxy")) {
      try {
        sessionStorage.setItem("aip:lastNonGalaxyPath", loc);
      } catch { /* ignore */ }
    }
  }, [loc]);
  return null;
}

function TodayRouter() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const unsub = subscribeImpersonation(() => setTick((t) => t + 1));
    return unsub;
  }, []);
  // Wave B: mirror DailyRouter — BDR → BdrDaily, AE → AeDaily,
  // manager/executive/other → AeTodayCommandCenter (W40 team rollup).
  const session = getSession();
  if (session?.role === "ae") {
    return <AuthGuard><AeDaily /></AuthGuard>;
  }
  if (session?.role === "bdr") {
    return <AuthGuard><BdrDaily /></AuthGuard>;
  }
  return <AuthGuard><AeTodayCommandCenter /></AuthGuard>;
}

export default function App() {
  // Detect Supabase magic-link expiry hash params and route to /link-expired.
  useMagicLinkError();

  // Detect Supabase password-recovery deep links (#access_token=...&type=recovery=...)
  // and rewrite the fragment so wouter routes to /login. Login.tsx then
  // catches PASSWORD_RECOVERY via onAuthStateChange and shows the reset UI.
  usePasswordRecoveryRoute();

  // Listen for Supabase Auth state changes (magic link callbacks, token refresh, etc.)
  useEffect(() => {
    const { data: { subscription } } = supabase().auth.onAuthStateChange(
      async (event, session) => {
        if (event === "SIGNED_OUT") {
          // Session cleared externally — let guards redirect.
        }
        if (event === "PASSWORD_RECOVERY") {
          // Belt-and-suspenders: if we are not on /login when Supabase emits
          // PASSWORD_RECOVERY (e.g. hook above bailed out or the user opened
          // the link in a tab that was already on a route), force the route.
          // Wouter's hash router cannot match query strings, so we route to
          // plain /login and use sessionStorage to signal recovery mode.
          try { sessionStorage.setItem("aip:passwordRecovery", "1"); } catch {}
          const h = (window.location.hash || "").toLowerCase();
          if (!h.startsWith("#/login") && !h.startsWith("#/pilot/login")) {
            window.location.hash = "#/login";
          } else if (h.includes("?")) {
            // Strip any stray query string so wouter can match the route.
            const target = h.startsWith("#/pilot/login") ? "#/pilot/login" : "#/login";
            window.location.hash = target;
          }
        }
      }
    );
    return () => subscription.unsubscribe();
  }, []);

  // Silent localStorage answer recovery — fires once per browser on first
  // session restore after the 2026-05-11 saveAnswer Supabase fix.
  // Fire-and-forget; never surfaces to the rep.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase().auth.getSession();
        const uid = data.session?.user?.id;
        if (!uid || cancelled) return;
        runSilentRecovery(uid).catch(() => {});
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Wire the persistence-store toast handlers once at app startup so write
  // failures in account-plan-store surface to the user as a toast instead of
  // dying silently in console.error. Fix for the 4pm BDR walk on 2026-05-20.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ setToastHandler: setPlanToast }, { toast }] = await Promise.all([
          import("@/lib/account-plan-store"),
          import("@/hooks/use-toast"),
        ]);
        if (cancelled) return;
        setPlanToast((m: string) => {
          try { toast({ title: m, variant: "destructive" as any }); } catch {}
        });
      } catch {
        // optional wiring — never block app boot
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
    <Router hook={useHashLocation} hrefs={(href) => "#" + href}>
    <SprintViewProvider>
    <TourProvider>
      {/* W44.1: Record last non-galaxy path so Exit Galaxy pill can navigate back */}
      <GalaxyPathRecorder />
      <Switch>
        {/* Root: resolves to login or dashboard based on session */}
        <Route path="/" component={RootRoute} />

        {/* /home alias — older links and tour scripts still point here. Resolve by role. */}
        <Route path="/home" component={RootRoute} />

        {/* Public auth — both /login and /pilot/login work */}
        <Route path="/login" component={Login} />
        {/* /pilot/login — Pilot Program entry point with early-access band */}
        <Route path="/pilot/login" component={PilotLogin} />
        {/* onboarding/pilot-v1 — Invitation acceptance for T3 pilot cohort */}
        <Route path="/accept-invite" component={AcceptInvite} />
        <Route path="/link-expired" component={LinkExpired} />

        {/* S-CP10-4 dev smoke route — no auth gate; remove or guard before prod */}
        <Route path="/dev/timing-gate" component={TimingGateSmokeDemo} />

        {/* Emergency logout via URL — useful in PWA standalone mode */}
        <Route path="/logout" component={LogoutRoute} />

        {/* ── Short-form role routes: redirect to canonical dashboard paths ── */}
        {/* W35: /dashboard (singular) was a dead 404 — redirect to canonical /dashboards */}
        <Route path="/dashboard">
          <Redirect to="/dashboards" />
        </Route>
        <Route path="/ae">
          <Redirect to="/dashboards/daily" />
        </Route>
        <Route path="/se">
          <Redirect to="/dashboards/se" />
        </Route>
        {/* /#/marketing now lands W55 MarketingHome (conversion-diagnostic dashboard 2026-05-29).
            MarketingAssistPage is preserved at /#/marketing/assist for backward compat.
            Operator-shell marketing dashboard is still reachable at /#/dashboards/marketing. */}

        {/* ── Hash-path aliases for common direct-nav routes ── */}
        <Route path="/accounts">
          <AuthGuard><AccountList /></AuthGuard>
        </Route>
        {/* /methodology route removed here — correct route is at bottom of file (P1 fix A-012) */}
        <Route path="/add-prospect">
          <AuthGuard><NewAccount /></AuthGuard>
        </Route>
        <Route path="/upload-contacts">
          <AuthGuard><UploadContacts /></AuthGuard>
        </Route>
        <Route path="/contacts/holding">
          <AuthGuard><HoldingQueue /></AuthGuard>
        </Route>
        {/* W2-Alias: /holding-queue → canonical /contacts/holding */}
        <Route path="/holding-queue">
          <Redirect to="/contacts/holding" />
        </Route>

        {/* Protected app routes */}
        <Route path="/legacy">
          <AuthGuard><AccountList /></AuthGuard>
        </Route>
        <Route path="/manager">
          <AuthGuard><ManagerHub /></AuthGuard>
        </Route>
        <Route path="/manager-legacy">
          <ManagerLegacyRetired />
        </Route>
        {/* /#/executive — Executive landing. 2026-05-16: routed directly to
            ExecutiveView (per-rep cards) to fix the double-OperatorShell overlap
            and the missing-nav issue on the old ExecutiveHub wrapper.
            P1-004 fix: requireRole guard blocks BDR/AE with redirect+toast. */}
        <Route path="/executive">
          <AuthGuard requireRole={["manager", "executive"]}><ExecutiveView /></AuthGuard>
        </Route>
        {/* New planning-trend-marketing surfaces */}
        <Route path="/my-plan">
          <AuthGuard><MyPlanPage /></AuthGuard>
        </Route>
        <Route path="/trend">
          <AuthGuard><TrendPage /></AuthGuard>
        </Route>
        <Route path="/marketing">
          <AuthGuard><MarketingHome /></AuthGuard>
        </Route>
        <Route path="/pipeline">
          <AuthGuard><Pipeline /></AuthGuard>
        </Route>
        <Route path="/daily">
          <DailyRouter />
        </Route>
        <Route path="/coaching">
          <AuthGuard requireRole={["manager", "executive"]}><Coaching /></AuthGuard>
        </Route>
        <Route path="/compelling-events">
          <AuthGuard><CompellingEvents /></AuthGuard>
        </Route>
        <Route path="/plan/:accountId">
          {(params) => <AuthGuard><PlanPresentation /></AuthGuard>}
        </Route>
        <Route path="/accounts/new">
          <AuthGuard><NewAccount /></AuthGuard>
        </Route>
        {/* W21: /accounts/:id/answer — Primary Answer Surface (canonical CP question entry) */}
        <Route path="/accounts/:id/answer">
          {(params) => <AuthGuard><AnswerLane /></AuthGuard>}
        </Route>
        {/* W8: /accounts/:id alias → redirects to canonical /account/:id (singular) */}
        <Route path="/accounts/:id">
          {(params) => <Redirect to={`/account/${params!.id}`} />}
        </Route>
        <Route path="/plan-report/:id">
          {(params) => <AuthGuard><PlanReport /></AuthGuard>}
        </Route>
        <Route path="/question-tree">
          <AuthGuard><QuestionTree /></AuthGuard>
        </Route>
        <Route path="/question-bank">
          <AuthGuard><QuestionBank /></AuthGuard>
        </Route>
        {/* W66.D: /decision-tree DELETED — subsumed by /methodology. No inbound links. */}
        {/* W54: /sales-path retired — redirect to /methodology where Path A/B content lives */}
        <Route path="/sales-path">
          <Redirect to="/methodology" />
        </Route>
        {/* W66.D: /bdr-sales-path DELETED — subsumed by /methodology. No inbound links. */}
        {/* W2-Alias: /opportunity-dna is canonical; /dna and /helix redirect here */}
        <Route path="/opportunity-dna">
          <AuthGuard><DNAPage /></AuthGuard>
        </Route>
        {/* W2-Alias: /dna → canonical /opportunity-dna */}
        <Route path="/dna">
          <Redirect to="/opportunity-dna" />
        </Route>
        {/* Pipeline DNA — AE aggregate; Team DNA — manager roster.
            Stub pages; helix views filled in by parallel subagent. */}
        <Route path="/pipeline-dna/:userId">
          {(params) => <AuthGuard><PipelineDNA /></AuthGuard>}
        </Route>
        {/* Bare /pipeline-dna lands on the current user's view */}
        <Route path="/pipeline-dna">
          <Redirect to="/pipeline-dna/me" />
        </Route>
        <Route path="/team-dna">
          <AuthGuard requireRole={["manager", "executive"]}><TeamDNA /></AuthGuard>
        </Route>
        {/* W66.D: /wall-poster DELETED — /galaxy is canonical (W49). */}
        {/* W4-Galaxy: 3D dimensional checkpoint flowchart */}
        <Route path="/galaxy/:accountId">
          {() => <Galaxy />}
        </Route>
        <Route path="/galaxy">
          <Galaxy />
        </Route>
        <Route path="/leaderboard">
          <AuthGuard requireRole={["manager", "executive"]}><Leaderboard /></AuthGuard>
        </Route>
        {/* W66.W6: /sales-plan is now Top-10 Account plan (all roles); QBR quota moved to /sales-plan/quota */}
        <Route path="/sales-plan/quota">
          <AuthGuard requireRole={["manager", "executive"]}><SalesPlanWorkbook /></AuthGuard>
        </Route>
        <Route path="/sales-plan">
          <AuthGuard><SalesPlanTopTen /></AuthGuard>
        </Route>
        <Route path="/question-review">
          <AuthGuard requireRole={["executive"]}><QuestionReview /></AuthGuard>
        </Route>
        {/* W2-Alias: /audit → canonical /audit-review */}
        <Route path="/audit">
          <Redirect to="/audit-review" />
        </Route>
        <Route path="/audit-review">
          <AuthGuard><AuditReview /></AuthGuard>
        </Route>
        <Route path="/audit-v2">
          <AuthGuard><AuditReviewV2 /></AuthGuard>
        </Route>
        <Route path="/preview/dashboards">
          {/* W2-Alias v2: preserve W2-RBAC executive gate, redirect to canonical /dashboards-preview */}
          <AuthGuard requireRole={["executive"]}><Redirect to="/dashboards-preview" /></AuthGuard>
        </Route>
        <Route path="/plan-vs-actual">
          <AuthGuard><PlanVsActual /></AuthGuard>
        </Route>

        {/* Dashboards hub */}
        {/* Deep-link routes — open Dashboards with a specific role tab */}
        {/* Direction A pilot — these 5 routes use the new OperatorShell */}
        <Route path="/dashboards/bdr">
          <AuthGuard requireRole={["bdr", "manager", "executive"]}><BdrView /></AuthGuard>
        </Route>
        <Route path="/dashboards/ae">
          <AuthGuard requireRole={["ae", "manager", "executive"]}><AeView /></AuthGuard>
        </Route>
        <Route path="/dashboards/daily">
          <AuthGuard requireRole={["ae", "manager", "executive"]}><AeView /></AuthGuard>
        </Route>
        <Route path="/dashboards/marketing">
          {/* W48: New marketing dashboard with FunnelVisual, Calendar, HookLibrary, LinkedIn, Audit, Accountability */}
          <AuthGuard requireRole={["marketing", "manager", "executive"]}><MarketingDashboardShell /></AuthGuard>
        </Route>
        <Route path="/dashboards/manager">
          <AuthGuard requireRole={["manager", "executive"]}><ManagerView /></AuthGuard>
        </Route>
        <Route path="/dashboards/management">
          <AuthGuard requireRole={["manager", "executive"]}><ManagerView /></AuthGuard>
        </Route>
        <Route path="/dashboards/executive">
          <AuthGuard requireRole={["manager", "executive"]}><ExecutiveView /></AuthGuard>
        </Route>
        <Route path="/dashboards/se">
          <Dashboards />
        </Route>
        <Route path="/dashboards/all">
          <Dashboards />
        </Route>
        <Route path="/dashboards">
          <AuthGuard><Dashboards /></AuthGuard>
        </Route>
        {/* Public mock for Bryan to review before going live — no auth */}
        <Route path="/dashboards-preview">
          <Dashboards />
        </Route>
        {/* W2-Alias: /intake → canonical /rapid-intake */}
        <Route path="/intake">
          <Redirect to="/rapid-intake" />
        </Route>
        <Route path="/rapid-intake">
          <RapidIntake />
        </Route>

        {/* Activity logs — no auth gate during preview */}
        <Route path="/log/bdr">
          <BdrActivityLog />
        </Route>
        <Route path="/log/ae">
          <AeActivityLog />
        </Route>
        <Route path="/log/marketing">
          <MarketingActivityLog />
        </Route>
        <Route path="/log/marketing/event/:id">
          {(params) => <MarketingEventLog />}
        </Route>

        {/* Cadence engine — daily execution view (BDR + AE) */}
        <Route path="/cadence/builder">
          <CadenceBuilder />
        </Route>
        <Route path="/cadence/bdr">
          <CadenceQueue role="bdr" />
        </Route>
        <Route path="/cadence/ae">
          <CadenceQueue role="ae" />
        </Route>
        <Route path="/cadence/reporting">
          <CadenceReporting />
        </Route>
        {/* W37-A: /cadence — BDR Cadence Queue (direct, auth-gated, BDR + AE) */}
        <Route path="/cadence">
          <AuthGuard requireRole={["bdr", "ae", "manager", "executive"]}><CadencePage /></AuthGuard>
        </Route>
        {/* W2-Alias: /cadence/queue → canonical /cadence/bdr */}
        <Route path="/cadence/queue">
          <Redirect to="/cadence/bdr" />
        </Route>

        {/* Settings — hub landing page with role-filtered cards */}
        <Route path="/settings">
          <AuthGuard><SettingsHub /></AuthGuard>
        </Route>
        <Route path="/settings/account">
          <AuthGuard><AccountSettings /></AuthGuard>
        </Route>
        <Route path="/settings/email">
          <EmailSettings />
        </Route>

        <Route path="/settings/override-policy">
          <AuthGuard requireRole={["manager", "executive"]}><OverridePolicy /></AuthGuard>
        </Route>
        <Route path="/settings/integrations">
          <Integrations />
        </Route>
        <Route path="/settings/company">
          <AuthGuard><CompanySettings /></AuthGuard>
        </Route>

        {/* Audit log viewer (manager/executive) */}
        <Route path="/admin/audit">
          <AuthGuard><AuditLogView /></AuthGuard>
        </Route>

        {/* Legal pages (no auth) */}
        <Route path="/legal/terms">
          <TermsOfService />
        </Route>
        <Route path="/legal/privacy">
          <PrivacyPolicy />
        </Route>
        <Route path="/todays-brief">
          <TodaysBrief />
        </Route>
        {/* /#/today — role-routed: AE → AeDaily, BDR → BdrDaily, else → TodayBriefPage */}
        <Route path="/today">
          <TodayRouter />
        </Route>

        {/* W66.B: Operator Workspace — unified daily home for AEs, BDRs, Managers, Executives */}
        {/* Marketing role auto-redirects to /marketing within the component */}
        <Route path="/workspace">
          <AuthGuard><WorkspacePage /></AuthGuard>
        </Route>

        {/* W66.F: Onboarding — first-login oriented landing (5-step, 90s flow) */}
        <Route path="/onboarding">
          <AuthGuard><OnboardingPage /></AuthGuard>
        </Route>

        {/* W66.H: Funnel — MQL→SQL→Opportunity→Won live-data funnel (all authenticated roles) */}
        <Route path="/funnel">
          <AuthGuard><FunnelPage /></AuthGuard>
        </Route>

        {/* Document Library — no auth during preview */}
        {/* W66.D: /library DELETED — Document Library orphaned, zero inbound links. */}

        {/* Template Library — cadence scripts catalog (browse / copy) */}
        <Route path="/templates">
          <AuthGuard><TemplateLibrary /></AuthGuard>
        </Route>

        {/* Marketing Activity Plan — no auth during preview */}
        <Route path="/marketing/plan">
          <MarketingPlan />
        </Route>
        <Route path="/marketing/calendar">
          <MarketingCalendar />
        </Route>
        <Route path="/marketing/hooks/:slug">
          <MarketingHookDetail />
        </Route>
        <Route path="/marketing/hooks">
          <MarketingHooks />
        </Route>

        {/* Lead-to-AE ribbon */}
        <Route path="/leads/new">
          <AuthGuard><AddLead /></AuthGuard>
        </Route>
        {/* P0-5 — Route-to-BDR step */}
        <Route path="/leads/:id/route-bdr">
          {(params) => <AuthGuard><RouteBdr /></AuthGuard>}
        </Route>
        <Route path="/leads/:id/qualify">
          {(params) => <AuthGuard><LeadQualify /></AuthGuard>}
        </Route>
        <Route path="/leads/:id/assign">
          {(params) => <AuthGuard><LeadAssign /></AuthGuard>}
        </Route>
        <Route path="/leads/:id/success">
          {(params) => <AuthGuard><LeadSuccess /></AuthGuard>}
        </Route>

        {/* BDR discovery + Pilot routes removed in v3.17 Partner purge */}
        {/* ── W45.2: Command View — MUST come before /account/:id/:tab ────────────── */}
        {/* Specific pattern beats the generic :tab wildcard only if listed first in Switch */}
        <Route path="/account/:id/command-view">
          {() => <AuthGuard><AccountCommandViewPage /></AuthGuard>}
        </Route>
        <Route path="/account/:id">
          {/* W39: key={params.id} forces full remount (including error boundary reset)
              when navigating between different accounts. This is the belt-and-suspenders
              cascade stop alongside AccountScorecardErrorBoundary.getDerivedStateFromError. */}
          {(params) => <AuthGuard><AccountScorecard key={params!.id} /></AuthGuard>}
        </Route>
        <Route path="/account/:id/:tab">
          {(params) => <AuthGuard><AccountScorecard key={params!.id} /></AuthGuard>}
        </Route>
        <Route path="/account/:id/dimension/:dim">
          {(params) => <AuthGuard><DimensionFlow /></AuthGuard>}
        </Route>

        {/* ── Reporting deep-views — management dashboards ─────────────────────── */}
        {/* ── Reports (executive deep-dives) ─────────────────────────────────────── */}
        <Route path="/reports/source-attribution">
          <SourceAttribution />
        </Route>
        <Route path="/reports/rep-quota">
          <RepQuota />
        </Route>
        {/* P1-004 fix: all executive report pages gated to manager/executive only */}
        <Route path="/reports/composite-scoring">
          <AuthGuard requireRole={["manager", "executive"]}><CompositeScoringReport /></AuthGuard>
        </Route>
        <Route path="/reports/forecast-rollup">
          <AuthGuard requireRole={["manager", "executive"]}><ForecastRollupReport /></AuthGuard>
        </Route>
        <Route path="/reports/escalation-queue">
          <AuthGuard requireRole={["manager", "executive"]}><EscalationQueueReport /></AuthGuard>
        </Route>
        <Route path="/reports/push-adequacy">
          <AuthGuard requireRole={["manager", "executive"]}><PushAdequacyReport /></AuthGuard>
        </Route>
        <Route path="/reports/push-reentry">
          <AuthGuard requireRole={["manager", "executive"]}><PushReentryReport /></AuthGuard>
        </Route>
        <Route path="/reports/external-consultants">
          <AuthGuard requireRole={["manager", "executive"]}><ExternalConsultantsReport /></AuthGuard>
        </Route>
        <Route path="/reports/soft-stop-audit">
          <AuthGuard requireRole={["manager", "executive"]}><SoftStopAuditReport /></AuthGuard>
        </Route>
        <Route path="/manager/soft-stop-queue">
          <AuthGuard requireRole={["manager", "executive"]}><SoftStopQueue /></AuthGuard>
        </Route>
        {/* ─────────────────────────────────────────────────────────────────────────── */}

        {/* P1-004 fix: reporting hub and team scoreboard gated to manager/executive only */}
        <Route path="/reporting">
          <AuthGuard requireRole={["manager", "executive"]}><ReportingHub /></AuthGuard>
        </Route>
        <Route path="/reporting/team">
          <AuthGuard requireRole={["manager", "executive"]}><TeamScoreboard /></AuthGuard>
        </Route>
        <Route path="/reporting/team-scoreboard">
          <AuthGuard requireRole={["manager", "executive"]}><TeamScoreboard /></AuthGuard>
        </Route>
        {/* W11: bare /reporting/rep (no id) — AE selector landing */}
        <Route path="/reporting/rep">
          <AuthGuard requireRole={["manager", "executive"]}><RepSelector /></AuthGuard>
        </Route>
        <Route path="/reporting/rep/:id">
          {(params) => <RepDetail repId={params.id} />}
        </Route>
        <Route path="/reporting/accounts">
          <AccountsInFlight />
        </Route>
        <Route path="/reporting/accounts-in-flight">
          <AccountsInFlight />
        </Route>
        <Route path="/reporting/source-funnel">
          <SourceFunnel />
        </Route>
        <Route path="/reporting/data-hygiene">
          <DataHygiene />
        </Route>
        <Route path="/reporting/account-detail">
          <AccountDetail />
        </Route>
        {/* ────────────────────────────────────────────────────────────────────────── */}

        {/* ── Helix Score Demo ──────────────────────────────────────────────────────── */}
        {/* W2-Alias: /helix and /helix-demo → canonical /opportunity-dna */}
        <Route path="/helix">
          <Redirect to="/opportunity-dna" />
        </Route>

        <Route path="/helix-demo">
          <Redirect to="/opportunity-dna" />
        </Route>
        {/* Helix Score Galaxy (decorative 3D — preserved for reference, not in nav) */}
        <Route path="/helix-galaxy">
          <HelixScoreDemo />
        </Route>
        {/* ──────────────────────────────────────────────────────────────────────────── */}

        {/* ── Admin: Support Queue ─────────────────────────────────────────────────── */}
        <Route path="/admin/support">
          <AuthGuard><SupportQueue /></AuthGuard>
        </Route>
        {/* ── Admin: Discipline module — Sprint 1 wiring ───────────────────────────── */}
        <Route path="/admin/discipline">
          <AuthGuard requireRole={["manager", "executive"]}>
            <DisciplineAdminPage />
          </AuthGuard>
        </Route>
        {/* ── Admin: Tier-1 Activity Audit ─────────────────────────────────────────── */}
        <Route path="/admin/activity-anomalies">
          <AuthGuard requireRole={["manager", "executive"]}>
            <ActivityAnomalies />
          </AuthGuard>
        </Route>
        <Route path="/admin/activity-audit">
          <AuthGuard requireRole={["manager", "executive"]}>
            <ActivityAnomalies />
          </AuthGuard>
        </Route>
        <Route path="/admin/activity-policy">
          <AuthGuard requireRole={["manager", "executive"]}>
            <ActivityCapturePolicy />
          </AuthGuard>
        </Route>
        {/* ──────────────────────────────────────────────────────────────────────────── */}

        {/* ── Missing-route P0 fixes ──────────────────────────────────────────────── */}
        <Route path="/plans">
          <AuthGuard><PlansHome /></AuthGuard>
        </Route>
        <Route path="/forecast">
          <AuthGuard><ForecastView /></AuthGuard>
        </Route>
        <Route path="/reports">
          <AuthGuard><ReportsHome /></AuthGuard>
        </Route>
        {/* W46 — chart-first dashboards (/dashboards/charts alongside /reports) */}
        <Route path="/dashboards/charts">
          <AuthGuard><DashboardsIndex /></AuthGuard>
        </Route>
        <Route path="/dashboards/pipeline-forecast">
          <AuthGuard><DashboardsIndex /></AuthGuard>
        </Route>
        <Route path="/dashboards/account-health">
          <AuthGuard><DashboardsIndex /></AuthGuard>
        </Route>
        <Route path="/admin">
          <AuthGuard><AdminHome /></AuthGuard>
        </Route>
        <Route path="/calendar">
          <AuthGuard><CalendarView /></AuthGuard>
        </Route>
        {/* W66.D: /hooks DELETED — orphaned; canonical surface is /marketing/hooks. Inbound link in CalendarView.tsx fixed to /marketing/hooks. */}
        <Route path="/activity-log">
          <AuthGuard><ActivityLogPage /></AuthGuard>
        </Route>
        {/* D-019: /activity redirect so bookmarks and old links survive */}
        <Route path="/activity">
          <Redirect to="/activity/audit" />
        </Route>
        {/* ── Activity Audit — weekend scaffold (Option A) ────────────── */}
        {/* Bug-D fix (2026-05-20): /activity-audit redirect so old nav links resolve */}
        <Route path="/activity-audit">
          <Redirect to="/activity/audit" />
        </Route>
        <Route path="/activity/audit">
          <AuthGuard><ActivityAuditPage /></AuthGuard>
        </Route>
        <Route path="/activity/drilldown">
          <AuthGuard><ActivityAuditDrilldown /></AuthGuard>
        </Route>
        {/* ──────────────────────────────────────────────────────────────────────────── */}

        <Route path="/methodology">
          <Methodology />
        </Route>

        {/* ── Checkpoint Flowcharts — CP1-CP10 v4 combined view (FixE) ── */}
        <Route path="/flowcharts">
          <AuthGuard><Flowcharts /></AuthGuard>
        </Route>

        {/* ── W66.X17: Reference Library — doctrine, manifestos, CP models ─ */}
        {/* 2026-06-11: LinkedIn article reader — must match BEFORE /library/:slug */}
        <Route path="/library/linkedin/:slug">
          <AuthGuard><LinkedInArticle /></AuthGuard>
        </Route>
        <Route path="/library/:slug">
          <AuthGuard><ReferenceArticle /></AuthGuard>
        </Route>
        <Route path="/library">
          <AuthGuard><ReferenceLibrary /></AuthGuard>
        </Route>

        {/* ── Operating Manuals — in-platform How-To library ────────────── */}
        <Route path="/manuals">
          <ManualsHome />
        </Route>
        <Route path="/manuals/marketing">
          <MarketingManual />
        </Route>
        <Route path="/manuals/bdr">
          <BdrManual />
        </Route>
        <Route path="/manuals/ae">
          <AeManual />
        </Route>
        <Route path="/manuals/manager">
          <ManagerManual />
        </Route>
        <Route path="/manuals/executive">
          <ExecutiveManual />
        </Route>
        {/* ──────────────────────────────────────────────────────────────── */}

        {/* ── Opportunity Calculator (manufacturing pilot) ────────────────── */}
        <Route path="/calculator">
          <AuthGuard><OpportunityCalculatorPage /></AuthGuard>
        </Route>

        {/* ── Opportunity Calculator v2.3 Preview (branching) ────────────── */}
        <Route path="/calculator-v3">
          <AuthGuard><OpportunityCalculatorPageV3 /></AuthGuard>
        </Route>

        {/* ── Tower (single-deal Renaissance Center silhouette) ─────────── */}
        <Route path="/tower">
          <AuthGuard><Tower /></AuthGuard>
        </Route>

        {/* ── Pipeline Tower (multi-deal aggregate — sister view to /tower) ─── */}
        <Route path="/pipeline-tower">
          <AuthGuard><PipelineTower /></AuthGuard>
        </Route>

        {/* ── W54: Rapid Answer Mode — keyboard-driven CP1-CP10 answer flow ─── */}
        <Route path="/account/:id/answer">
          {(params) => <AuthGuard><RapidAnswer /></AuthGuard>}
        </Route>

        {/* ── W45.2: /command-view — Portfolio grid (per-account route moved up, before /:tab) ─ */}
        <Route path="/command-view">
          <AuthGuard><CommandViewPage /></AuthGuard>
        </Route>

        {/* ── W65: Opportunity pages ────────────────────────────────────── */}
        <Route path="/opportunities">
          <AuthGuard><OpportunitiesIndex /></AuthGuard>
        </Route>
        <Route path="/opportunity/new">
          <AuthGuard><NewOpportunity /></AuthGuard>
        </Route>
        <Route path="/opportunity/:id">
          {(params) => <AuthGuard><OpportunityDetail /></AuthGuard>}
        </Route>
        <Route path="/account/:accountId/opportunities">
          {(params) => <AuthGuard><OpportunitiesIndex /></AuthGuard>}
        </Route>
        <Route path="/account/:accountId/opportunity/new">
          {(params) => <AuthGuard><NewOpportunity /></AuthGuard>}
        </Route>
        {/* ── W66.G: Organizational Mapping ────────────────────────────────── */}
        <Route path="/account/:accountId/org-map">
          {(params) => <AuthGuard><AccountOrgMap /></AuthGuard>}
        </Route>
        {/* ── W66.J: Jumpstart 30/60/90 ─────────────────────────────────────── */}
        <Route path="/jumpstart">
          <AuthGuard><JumpstartPage /></AuthGuard>
        </Route>

        {/* ── W66.X16: Guided tour — four role perspectives ────────────────── */}
        <Route path="/tour">
          <Tour />
        </Route>
        {/* ──────────────────────────────────────────────────────────────────────────── */}

        {/* Reserved placeholder routes — surfaces named in brand vocabulary but not yet shipped. */}
        <Route path="/booking">
          <ComingSoon feature="Booking calendar" description="Integrated meeting booking will ship in a follow-on release. For now, use the Calendly link from any account page." />
        </Route>
        <Route path="/book-a-call">
          <ComingSoon feature="Book a call" description="Integrated meeting booking will ship in a follow-on release. For now, use the Calendly link from any account page." />
        </Route>
        <Route path="/schedule">
          <ComingSoon feature="Schedule" description="A unified scheduling surface is on the roadmap. Use the Today view and your Cadence queue in the meantime." />
        </Route>
        <Route path="/outreach">
          <ComingSoon feature="Outreach" description="A dedicated outreach console is on the roadmap. BDR Cadence and AE Cadence cover outreach activity today." />
        </Route>
        <Route path="/1on1">
          <ComingSoon feature="1:1 reviews" description="Structured 1:1 reviews are on the roadmap. The Manager Coaching view is the closest equivalent today." />
        </Route>
        <Route path="/discovery">
          <ComingSoon feature="Discovery workspace" description="A discovery-stage workspace is on the roadmap. Use the Question Tree and Sales Path on an account today." />
        </Route>

        <Route component={NotFound} />
      </Switch>

      <DocumentTitle />
      <Toaster />
      <TourOverlay />
      <InstallPrompt />
      {/* D-079: <QuickScoreFab /> removed */}
      <WhatsNewDrawer />
    </TourProvider>
    </SprintViewProvider>
    </Router>
    </QueryClientProvider>
  );
}
